import React, {useCallback, useEffect, useMemo, useState} from "react";
import axios from "../../utils/apiClient";
import {useAuth} from "../../context/authContext";
import cloneDeep from "lodash/cloneDeep";
import {getDisplayValue} from "../../utils/util";
import {Card, Col, Container, Form, Row} from "react-bootstrap";
import SpinnerLoading from "../common/components/spinner";
import "react-datepicker/dist/react-datepicker.css";
import "react-datepicker/dist/react-datepicker-cssmodules.css";
import AssessmentReport from "./AssessmentReport";
import ExportSummary from "../quickList/ExportSummary";
import Modals from "../../components/modals";
import {report_Url_Map, reportsWithoutParams, stateFullName} from "../../utils/textValueLists";

const componentName = "ReportMain";
const PLACEHOLDER_REPORT_TYPE = "Select Report Type";
const ACTIVITY_REPORTS_REQUIRING_PRACTICE = new Set([
  "Activity Report: Logins",
  "Activity Report: Chart Prep Tool",
  "Activity Report: Quality and Risk",
]);

const DEFAULT_SEARCH_PARAMS = {
  filterParams: [],
  quickListName: "",
  sortParams: [],
  pageNumber: 1,
  pageSize: 20,
};
const baseURLPC = `${process.env.REACT_APP_REST_API_BASE_URL}/v1/reports/`;

const addMissingParamError = (paramNames, paramName, message, errors) => {
  if (!paramNames.includes(paramName)) {
    errors.push(message);
  }
};

const addReportSpecificValidationErrors = (selectedReportType, paramNames, errors) => {
  if (ACTIVITY_REPORTS_REQUIRING_PRACTICE.has(selectedReportType)) {
    addMissingParamError(paramNames, "Practice Name", "Practice Name is Mandatory", errors);
  }

  if (selectedReportType === "Assessments Report") {
    addMissingParamError(paramNames, "Assessment Template", "Assessment Template is Mandatory", errors);
  } else if (selectedReportType === "Care Program Report") {
    addMissingParamError(paramNames, "Time Range", "Both Values in Time Range are Mandatory", errors);
    addMissingParamError(paramNames, "Status", "Status is Mandatory", errors);
  } else if (selectedReportType === "Complex Password Tracking Report") {
    addMissingParamError(paramNames, "Status", "Status is Mandatory", errors);
  } else if (selectedReportType === "AAWV Report" || selectedReportType === "Diagnosis Report") {
    addMissingParamError(paramNames, "Year", "Year is Mandatory", errors);
    addMissingParamError(paramNames, "Practice Name", "Practice Name is Mandatory", errors);
  }
};

const addParamValueValidationErrors = (param, errors) => {
  if (new Date(param.paramValue[0]) > new Date(param.paramValue[1])) {
    errors.push("Start date must be less than or equal to End date.");
  }

  if (param.paramName === "Time Range" && (param.paramValue.length < 2 || param.paramValue.includes(undefined))) {
    errors.push("Both Values in Time Range are Mandatory");
  }

  if (param.paramName === "Status" && (param.paramValue.length < 1 || param.paramValue.includes(undefined))) {
    errors.push("Status is Mandatory");
  }
};

const needsTimeRangeOrStatus = (selectedReportType, paramNames) => (
  selectedReportType !== "AAWV Report" &&
  selectedReportType !== "Diagnosis Report" &&
  !paramNames.some((x) => x.includes("Time Range") || x.includes("Status"))
);

export const ReportMain = () => {
  const [selectedClient, setSelectedClient] = useState("");
  const [modalMessage, setModalMessage] = useState(null);
  const [modalSize, setModalSize] = useState(null);
  const [modalTitle, setModalTitle] = useState(null);
  const [showExportSpinner, setShowExportSpinner] = useState(false);
  const [searchParameters, setSearchParameters] = useState({
    ...DEFAULT_SEARCH_PARAMS,
  });
  const [reportTypes, setReportTypes] = useState([]);
  const [errors, setErrors] = useState([]);
  const [selectedReportType, setSelectedReportType] = useState("");

  const auth = useAuth();
  const inputSources = useMemo(
    () => [...auth.getInputSources()].sort((a, b) => a.localeCompare(b)),
    [auth]
  );

  const findReportTypesForCLient = useCallback((id) => {
    const url = process.env.REACT_APP_REST_API_BASE_URL;
    axios
      .get(url + `/v1/reports/client-report-types?clientId=${id}`, {
        headers: {},
      })
      .then((response) => {
        setSelectedReportType(PLACEHOLDER_REPORT_TYPE);
        //reportDropDRef.current.value = 'Report Type'
        let arr = response.data;
        arr.splice(0, 0, PLACEHOLDER_REPORT_TYPE);
        setReportTypes(arr);
      })
      .catch((err) => {
        setModalMessage("Failed to get Report Types. Please try again.");
        setReportTypes([]);
      });
  }, []);

  useEffect(() => {
    if (inputSources.length > 0) {
      setSelectedClient(inputSources[0]);
      findReportTypesForCLient(getDisplayValue(stateFullName,inputSources[0]));
    }
  }, [findReportTypesForCLient, inputSources]);

  const handleReportChange = (e) => {
    const { value } = e.target;
    setSelectedReportType(value);
  };

  const handleInputSourceChange = (e) => {
    setSelectedClient(e.target.value);
    findReportTypesForCLient(getDisplayValue(stateFullName,e.target.value));
  };

  const handleFilterChange = (e, type) => {
    let searchParametersClone = {...searchParameters};
    if (checkForValidation(e)) {
      // Remove empty values
      searchParametersClone.filterParams = e.filter(Boolean);
      let searchParametersCloneBackend = cloneDeep(searchParametersClone);
      // async
      setSearchParameters(searchParametersClone);
      if (
          selectedClient &&
          searchParametersCloneBackend?.filterParams?.filter(
              (r) => r?.paramName === "clientID"
          ).length === 0
      ) {
        const clientIdObject = {
          comparison: "eq",
          displayValue: [selectedClient],
          paramName: "clientID",
          paramValue: [selectedClient],
        };
        searchParametersCloneBackend.filterParams.push(clientIdObject);
      }
      searchParametersCloneBackend.filterParams.forEach((param)=>{
        if (
          param.paramName === "Status" &&
          Array.isArray(param.paramValue) &&
          param.paramValue.length === 1 &&
          param.paramValue[0].toUpperCase() === "BOTH"
        ) {
          param.paramValue = ["OPEN_CARE_PATHS", "CLOSED_CARE_PATHS"];
        }
      });
      generateReport(searchParametersCloneBackend);
    }
  };

  const checkForValidation = (param) => {
    let errorArray = [];
    let loclVar = true;

    if(reportsWithoutParams.includes(selectedReportType)) {
      setErrors(errorArray);
      return loclVar;
    }

    if (param?.length) {
      const arrOfParamVal = param.map((e) => e.paramName);
      if (needsTimeRangeOrStatus(selectedReportType, arrOfParamVal)) {
        errorArray.push("Time Range is Mandatory");
        loclVar = false;
      } else {
        addReportSpecificValidationErrors(selectedReportType, arrOfParamVal, errorArray);
        param.forEach((e) => {
          if (e) {
            addParamValueValidationErrors(e, errorArray);
          }
        });
        loclVar = errorArray.length === 0;
      }
    } else {
      errorArray.push("Please enter all the mandatory fields");
      loclVar = false;
    }
    setErrors(errorArray);
    return loclVar;
  };

  const generateReport = (searchParameters) => {
    setShowExportSpinner(true);
    const repType = report_Url_Map[selectedReportType];
    axios
      .post(baseURLPC + repType, searchParameters, {
        headers: { "Content-Type": "application/json" },
      })
      .then(async (response) => {
        summary();
      })
      .catch((error) => {
      });
  };

  const summary = () => {
    const SUMMARY_URL = `${process.env.REACT_APP_REST_API_BASE_URL}/v1/patient-card/export-summary`;
    axios
      .get(SUMMARY_URL, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        setShowExportSpinner(false);
        setModalTitle("Export Summary");
        setModalSize("lg");
        setModalMessage(<ExportSummary exportSummary={response.data} />);
      })
      .catch((error) => {
        setShowExportSpinner(false);
        setModalTitle("Error");
        setModalMessage(error.response.data.message);
      });
  };

  return (
    <>
      <Modals
        show={errors.length}
        onHide={() => setErrors([])}
        title="Error"
        titleid={`${componentName}-errorModal`}
        size="md"
        completeBtn={"Close"}
        completeBtnId={`${componentName}-closeButton`}
        hideFooter={false}
        hideClickButton={true}
        showExtraButton={false}
        body={<>{errors.toString()}</>}
      />
      <Container fluid className="p-4">
        <Row>
          <Col>
            {showExportSpinner && (
              <SpinnerLoading />
            )}
            <Card>
              <Card.Body>
                <Row>
                  <Col sm={4} id={`${componentName}-clients`} className="mb-3">
                    <label htmlFor={`${componentName}-clientsValuse`} id={`${componentName}-clientsLabel`} className="d-block">Clients</label>
                    <Form.Control
                      as="select"
                      name="reportType"
                      value={selectedClient || ""}
                      id={`${componentName}-clientsValuse`}
                      className="form-select"
                      onChange={(e) => handleInputSourceChange(e)}
                    >
                      {inputSources.map((r, idx) => (
                        <option id={`${componentName}-clientsItems-${idx}`} key={r} value={r}>
                          {getDisplayValue(stateFullName, r)}
                        </option>
                      ))}
                    </Form.Control>
                  </Col>
                  <Col sm={4} id={`${componentName}-reportType`} className="mb-3">
                    {reportTypes && reportTypes.length > 0 && (
                      <>
                        <label htmlFor={`${componentName}-reportTypeValue`} id={`${componentName}-reportTypeLabel`} className="d-block">Report Type</label>
                        <Form.Control
                          as="select"
                          name="reportType"
                          value={selectedReportType || PLACEHOLDER_REPORT_TYPE}
                          id={`${componentName}-reportTypeValue`}
                          className="form-select"
                          onChange={(e) => handleReportChange(e)}
                        >
                          {reportTypes.map((r, idx) => (
                            <option id={`${componentName}-reportTypeItems-${idx}`} key={r} value={r}>
                              {r}
                            </option>
                          ))}
                        </Form.Control>
                      </>
                    )}
                  </Col>
                </Row>
                <Row>
                  <Col>
                    {reportTypes && reportTypes.length > 0 && (
                      <AssessmentReport
                        value={searchParameters.filterParams}
                        reportType={selectedReportType}
                        client={selectedClient}
                        handleFilterChange={handleFilterChange}
                      />
                    )}
                  </Col>
                </Row>
              </Card.Body>
            </Card>
          </Col>
        </Row>
        <Modals
          show={modalMessage != null}
          onHide={() => setModalMessage(null)}
          title={modalTitle}
          size={modalSize || "md"}
          hideClickButton={false}
          showExtraButton={false}
          hideFooter={true}
          body={modalMessage}
        />
      </Container>
    </>
  );
};

export default ReportMain;
